"use client";

import type { ReactNode } from "react";
import dynamic from "next/dynamic";
import { useReducedMotion } from "motion/react";
import type { CaseData } from "@/cms/models";
import PropertyGalleryCard from "@/components/PropertyGalleryCard";
import PropertyEditorialCard from "@/components/PropertyEditorialCard";
import PropertyListCard from "@/components/PropertyListCard";
import PropertyRowCard from "@/components/PropertyRowCard";
import PropertyDuoCard from "@/components/PropertyDuoCard";
import TiltCard from "@/components/ui/TiltCard";
import MapIllustration from "@/guide/MapIllustration";
import CardMotion from "../CardMotion";
import { getCaseUrl } from "@/utils/case-url";
import type { PropertyListSettings, PropertyListView } from "./model";

const MapWrapper = dynamic(() => import("@/components/ui/MapWrapper"), {
  ssr: false,
});
type Shadow = "none" | "little" | "lots";

/** The Gitter card, which the map's popover shows too. */
export function GridCard({
  property,
  list,
  imageRatio,
  popover = false,
}: {
  property: CaseData;
  list: PropertyListSettings;
  imageRatio?: string;
  popover?: boolean;
}) {
  const cardPhoto = !!list.lookSystem && !popover;
  return list.gridCard === "editorial" ? (
    <PropertyEditorialCard
      property={property}
      layout="grid"
      options={{
        ...list.editorialCard,
        ...(popover ? { actionVisibility: "always" } : {}),
        ...(imageRatio ? { imageRatio } : {}),
      }}
      cardPhoto={cardPhoto}
    />
  ) : (
    <PropertyGalleryCard
      property={property}
      shadow={list.cardShadow as Shadow}
      lookSystem={list.lookSystem}
      cardPhoto={cardPhoto}
    />
  );
}

function card(
  view: Exclude<PropertyListView, "map">,
  property: CaseData,
  list: PropertyListSettings,
  imageRatio?: string,
) {
  const shadow = list.cardShadow as Shadow;
  if (view === "grid")
    return <GridCard property={property} list={list} imageRatio={imageRatio} />;
  if (view === "row")
    return (
      <PropertyRowCard
        property={property}
        shadow={shadow}
        lookSystem={list.lookSystem}
        cardPhoto={list.lookSystem}
      />
    );
  if (view === "duo")
    return (
      <PropertyDuoCard
        property={property}
        shadow={shadow}
        lookSystem={list.lookSystem}
        cardPhoto={list.lookSystem}
      />
    );
  return list.listCard === "editorial" ? (
    <PropertyEditorialCard
      property={property}
      layout="horizontal"
      options={{ ...list.editorialCard, ...(imageRatio ? { imageRatio } : {}) }}
      cardPhoto={list.lookSystem}
    />
  ) : (
    <PropertyListCard property={property} cardPhoto={list.lookSystem} />
  );
}

/** The cases in one view: Gitter in the section's columns, Liste, Række and Duo one card per row as on the old list
 *  (a Duo card is itself three columns wide). Tilt and fly-in stay off for visitors who prefer reduced motion. */
export function PropertyListCards({
  items,
  view,
  list,
  imageRatio,
}: {
  items: CaseData[];
  view: Exclude<PropertyListView, "map">;
  list: PropertyListSettings;
  imageRatio?: string;
}) {
  const reduced = useReducedMotion();
  const tilts =
    list.tilt &&
    !reduced &&
    (view !== "grid" || list.gridCard === "gallery") &&
    (view !== "list" || list.listCard === "list");
  const wrap = (content: ReactNode) =>
    tilts ? <TiltCard>{content}</TiltCard> : content;
  return (
    <div
      className={`${view === "grid" ? "pf-grid" : "pf-list"} pf-plist-cards`}
      data-view={view}
    >
      {items.map((property, index) => (
        <CardMotion
          key={property.id}
          flyIn={list.flyIn}
          index={index}
          className="pf-items pf-plist-card"
          data-card-index={index}
        >
          {wrap(card(view, property, list, imageRatio))}
        </CardMotion>
      ))}
    </div>
  );
}

/** Kortvisning: a marker per loaded case with coordinates; the links are in the HTML too, for screen readers and
 *  crawlers. With `illustration` (the CMS preview and the guide, which never contact the map service) it draws the
 *  guide's map illustration instead. */
export function PropertyListMap({
  items,
  total,
  list,
  imageRatio,
  withLinks = true,
  illustration = false,
}: {
  items: CaseData[];
  total: number;
  list: PropertyListSettings;
  imageRatio?: string;
  withLinks?: boolean;
  illustration?: boolean;
}) {
  return (
    <div
      className="pf-plist-map"
      data-re-reveal={list.lookSystem ? "" : undefined}
    >
      <div className="pf-plist-map-frame" data-search>
        {illustration ? (
          <MapIllustration
            markerLabel="Boligerne"
            caption="Kortet viser boligernes placering."
          />
        ) : (
          <MapWrapper
            markers={items
              .filter((item) => !!item.latitude && !!item.longitude)
              .map((property) => ({
                latitude: property.latitude!,
                longitude: property.longitude!,
                popover: (
                  <GridCard
                    property={property}
                    list={list}
                    imageRatio={imageRatio}
                    popover
                  />
                ),
              }))}
          />
        )}
      </div>
      {total > items.length && (
        <p className="pf-plist-map-note">
          Kortet viser de {items.length} første af {total}
        </p>
      )}
      {withLinks && (
        <ul className="sr-only">
          {items.map((property) => (
            <li key={property.id}>
              <a href={getCaseUrl(property)}>
                {property.advertisementAddressOrDisplayName}
              </a>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
